// Iter 755 (Kamel, Turin J-1) — 🎫 QR code d'embarquement mis en valeur : grand, lisible, plein écran d'un appui.
// Lutte contre la fracture numérique : une seule chose à faire en montant dans le car = montrer cet écran.
import React, { useEffect, useState } from 'react';
import { joursAvantDepart as joursAvant } from './BlocsFiche';

// Iter 758 (Kamel) — le QR ne doit pas être envahissant : visible uniquement la veille et le jour du car
// (aller : J-1 / J0 du départ ; retour : J-1 / J0 du retour si la fiche a un car retour). Jamais pendant le séjour, plus rien le lendemain.
export const qrImminent = (f) => {
  if (!f?.bus?.qr || f.statut === 'annulée') return null;
  const jd = joursAvant(f.date_depart);
  const jr = joursAvant(f.date_retour);
  if (!f.bus.retour_seul && jd !== null && jd >= 0 && jd <= 1) return { type: 'aller', j: jd };
  if (!f.bus.aller_seul && jr !== null && jr >= 0 && jr <= 1 && (f.date_retour !== f.date_depart)) return { type: 'retour', j: jr };
  return null;
};
export const ficheQrImminente = (fiches) => (fiches || []).find(f => qrImminent(f));

// Iter 757 (Kamel) — « Bus 1 : places 12, 13 » : le nom du car attribué, indispensable quand il y a plusieurs cars.
export const PlacesParBus = ({ f, grand = false, testId }) => {
  const pb = f.bus?.par_bus?.length ? f.bus.par_bus : (f.bus?.places?.length ? [{ bus: 'Votre car', places: f.bus.places, voyageurs: [] }] : []);
  if (!pb.length) return <p className={`mt-2 ${grand ? 'text-base' : 'text-xs'} text-amber-800 font-bold`}>Place attribuée par l'équipe à la montée.</p>;
  return (
    <div className={`mt-2 space-y-1 ${grand ? 'text-2xl' : 'text-lg'}`} data-testid={testId}>
      {pb.map((b, i) => (
        <div key={i} className="rounded-xl bg-[#1E3A5F] text-white px-3 py-2 inline-block mx-1">
          <span className="font-black">🚌 {b.bus}</span>
          <span className="font-black"> · Place{b.places.length > 1 ? 's' : ''} {b.places.join(', ')}</span>
          {f.bus?.plusieurs_bus && b.voyageurs?.length > 1 && <span className={`block ${grand ? 'text-sm' : 'text-[11px]'} font-normal opacity-90 mt-0.5`}>{b.voyageurs.map(v => `${v.nom} → ${v.place}`).join(' · ')}</span>}
        </div>
      ))}
      {f.bus?.plusieurs_bus && <p className={`${grand ? 'text-sm' : 'text-[11px]'} text-amber-800 font-bold`}>⚠️ Plusieurs cars ce jour-là : montez bien dans le <strong>{pb.map(b => b.bus).join(' et le ')}</strong>.</p>}
    </div>
  );
};

export const QrPleinEcran = ({ f, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[90] bg-white flex flex-col items-center justify-center p-6 text-center" onClick={onClose} data-testid={`qr-plein-ecran-${f.reg_id}`}>
      <p className="text-lg font-black text-[#1E3A5F]">🎫 {f.voyage}</p>
      <p className="text-sm text-gray-600 mb-3">{f.voyageurs.join(', ')}</p>
      <img src={f.bus.qr} alt="QR code d'embarquement" className="w-[min(80vw,80vh)] h-auto rounded-xl border-4 border-[#1E3A5F]" />
      <PlacesParBus f={f} grand testId={`qr-plein-ecran-bus-${f.reg_id}`} />
      <p className="mt-2 text-sm text-gray-700">Montrez cet écran à l'accompagnateur en montant dans le car.</p>
      <button type="button" className="mt-5 px-6 py-3 rounded-xl bg-[#1E3A5F] text-white font-bold" data-testid={`qr-plein-ecran-fermer-${f.reg_id}`}>Fermer</button>
    </div>
  );
};

export const QrEmbarquement = ({ f, taille = 200, compact = false }) => {
  const [plein, setPlein] = useState(false);
  if (!f.bus?.qr) return null;
  return (
    <div className={`text-center bg-white border-2 border-dashed border-[#1E3A5F] rounded-xl ${compact ? 'p-3' : 'p-4'}`} data-testid={`fiche-bus-qr-${f.reg_id}`}>
      <p className="text-sm font-black text-[#1E3A5F]">🎫 Mon QR code d'embarquement</p>
      <button type="button" onClick={() => setPlein(true)} className="block mx-auto mt-2" data-testid={`fiche-bus-qr-agrandir-${f.reg_id}`} aria-label="Agrandir le QR code">
        <img src={f.bus.qr} alt="QR code d'embarquement" width={taille} height={taille} className="mx-auto rounded-lg border" />
      </button>
      <PlacesParBus f={f} testId={`fiche-bus-qr-places-${f.reg_id}`} />
      <button type="button" onClick={() => setPlein(true)} className="mt-2 w-full py-3 rounded-xl bg-[#1E3A5F] text-white font-bold text-sm" data-testid={`fiche-bus-qr-btn-${f.reg_id}`}>🔍 Afficher en grand pour le scan</button>
      <p className="text-[11px] text-gray-600 mt-2">À présenter à l'accompagnateur en montant dans le car — il vaut pour <strong>tout votre groupe</strong>. Pensez à monter la luminosité de l'écran. Pas de téléphone ? Donnez simplement votre nom.</p>
      {plein && <QrPleinEcran f={f} onClose={() => setPlein(false)} />}
    </div>
  );
};

// Bandeau tout en haut de l'espace à J-1 / J0 / en cours.
export const BandeauQrDepart = ({ f, onOuvrirBus }) => {
  const im = qrImminent(f) || { type: 'aller', j: joursAvant(f.date_depart) };
  const titre = im.type === 'retour'
    ? (im.j === 0 ? '🔁 Retour aujourd\'hui : votre car vous ramène' : '🔁 Retour demain : votre car vous ramène')
    : (im.j === 0 ? "🚌 C'est aujourd'hui !" : '🧳 Départ demain !');
  return (
    <div className="bg-[#1E3A5F] text-white rounded-2xl p-4 shadow-lg" data-testid={`espace-bandeau-qr-${f.reg_id}`}>
      <p className="font-black text-base">{titre} — {f.voyage}</p>
      <p className="text-sm opacity-95 mt-0.5">Pour monter dans le car, une seule chose à faire : <strong>montrer ce QR code</strong> à l'accompagnateur.</p>
      <div className="mt-3 text-gray-900"><QrEmbarquement f={f} taille={220} /></div>
      {onOuvrirBus && <button type="button" onClick={onOuvrirBus} className="mt-2 w-full text-center text-xs underline opacity-90" data-testid={`espace-bandeau-qr-bus-${f.reg_id}`}>Voir « Mon bus et mon QR Code » (horaires, lieu de rendez-vous)</button>}
    </div>
  );
};
